import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IndicatorComponent } from '@kinet-open-network/client/shared/ui';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { ScrollerOptions } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Observable, map, of } from 'rxjs';

interface SelectProps extends FormlyFieldProps, FormlyFieldSelectProps {
  onFilter?: (field: FormlyFieldConfig, event: unknown) => void;

  /**
   * If set to true, the current value will be added to the options if the options are empty.
   * This is useful when the options are loaded asynchronously or they are not loaded on
   * purpose (e.g. when user doesn't have ability to load them) but the value itself is already set.
   *
   * With this option enabled, dropdown will show list of options with the current value
   * as the only option in the list. This cannot be used when the value and label are different,
   * because value is used as a label.
   *
   * @default false
   */
  appendValueOption?: boolean | ((value: any) => SelectOption);
  filter: boolean;
  loading?: boolean;
  allowCustomValue?: boolean;
  showClear?: boolean;
}

export interface FormlySelectFieldConfig
  extends FormlyFieldConfig<SelectProps> {}

type SelectOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

@Component({
  selector: 'kon-select-type',
  imports: [
    CommonModule,
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    IndicatorComponent,
  ],
  template: `
    <p-select
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(selectOptions | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [formControl]="formControl"
      [showClear]="!props.required || props.showClear"
      (onChange)="props.change && props.change(field, $event)"
      [virtualScroll]="true"
      [virtualScrollOptions]="scrollerOptions"
      [filter]="props.filter"
      [style]="{ width: '100%' }"
      (onFilter)="props.onFilter && props.onFilter(field, $event)"
      [placeholder]="props.placeholder"
      [loading]="props.loading"
      [editable]="props.allowCustomValue"
    >
      <ng-template let-item pTemplate="selectedItem">
        @if (item) {
          <div class="flex items-center gap-2">
            @if (item.color) {
              <kon-indicator [color]="item.color" />
            }
            @if (item.icon) {
              <i [class]="item.icon" [ngStyle]="icontStyle(item)"></i>
            }
            <div class="truncate" [title]="item.label">{{ item.label }}</div>
          </div>
        }
      </ng-template>
      <ng-template let-item pTemplate="item">
        <div class="flex items-center gap-2">
          @if (item.color) {
            <kon-indicator [color]="item.color" />
          }
          @if (item.icon) {
            <i [class]="item.icon" [ngStyle]="icontStyle(item)"></i>
          }
          <div class="truncate" [title]="item.label">{{ item.label }}</div>
        </div>
      </ng-template>
    </p-select>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySelectTypeComponent extends FieldType<
  FieldTypeConfig<SelectProps>
> {
  override defaultOptions = {
    props: {
      appendValueOption: false,
      filter: true,
    },
  };

  get selectOptions() {
    if (this.props.options) {
      if (this.props.options instanceof Observable) {
        return this.props.options.pipe(
          map((options) => this.mapOptions(options)),
          map((options) => this.appendValueOption(options)),
        );
      }

      return of(this.appendValueOption(this.mapOptions(this.props.options)));
    }

    return of([]);
  }

  scrollerOptions: ScrollerOptions = {
    showLoader: true,
    scrollHeight: '220px',
    itemSize: 42,
  };

  private mapOptions(options: any[]): SelectOption[] {
    const mappedOptions = options.map((option) => {
      if (
        typeof option === 'string' ||
        typeof option === 'number' ||
        typeof option === 'boolean' ||
        option === null ||
        option === undefined
      ) {
        return { label: option?.toString() ?? '', value: option };
      }

      if (typeof option === 'object') {
        const getValue = (getter: string | ((option: any) => string)) => {
          if (typeof getter === 'string') {
            return option[getter];
          } else if (typeof getter === 'function') {
            return getter(option);
          }

          return undefined;
        };

        return {
          label: this.props.labelProp
            ? getValue(this.props.labelProp)
            : option.label,
          value: this.props.valueProp
            ? getValue(this.props.valueProp)
            : option.value,
          ...option,
        };
      }

      return option;
    });

    return mappedOptions;
  }

  /**
   * This method will add current value to the options if the options are empty,
   * this is useful when the options are loaded asynchronously and the value is already set.
   * This only works when the value and label are the same, because value is used as a label.
   *
   * @param options
   * @returns options with current value if the options are empty
   */
  private appendValueOption(
    options: SelectOption[] | undefined,
  ): SelectOption[] {
    const value = this.formControl.value;
    if (this.props.appendValueOption && value) {
      const isOptionPresent = options?.some((option) => option.value === value);
      if (!isOptionPresent) {
        return [
          typeof this.props.appendValueOption === 'function'
            ? this.props.appendValueOption(value)
            : {
                label: value,
                value,
              },
          ...(options ?? []),
        ];
      }
    }
    return options ?? [];
  }

  icontStyle(item: any) {
    const iconColor = item.iconColor;
    return iconColor ? { color: iconColor } : undefined;
  }
}
